"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getCourseDetailsApiV1CoursesCourseIdGet,
  getProactiveStatusApiV1LessonsLessonIdProactiveStatusGet,
  triggerProactiveGenerationApiV1LessonsLessonIdProactiveGenerationPost,
} from "@/src/api/sdk.gen";
import type { CourseDetail } from "@/components/student/course-detail/types";
import {
  PREPARING_POLL_INTERVAL_MS,
  PREPARING_POLL_MAX_MS,
  LESSON_PROACTIVE_POLL_FAST_ROUNDS,
  LESSON_PROACTIVE_POLL_INITIAL_MS,
  LESSON_PROACTIVE_POLL_MAX_MS,
  LESSON_PROACTIVE_POLL_STEADY_MS,
  type LessonPersonalizePhase,
  type ProactiveStatusPayload,
  fetchProactiveLessonStatus,
  interpretProactiveFetchOutcome,
  lessonPersonalizingHeadline,
  normalizeProactiveStatusBody,
} from "@/lib/student/proactive-status";

export type UseProactiveCoursePreparingPollOptions = {
  courseId: string;
  canonicalLessonId: number | null;
  enabled: boolean;
  onCourseData: (course: CourseDetail) => void;
  pollIntervalMs?: number;
  maxPollMs?: number;
  pauseWhenHidden?: boolean;
};

export function useProactiveCoursePreparingPoll({
  courseId,
  canonicalLessonId,
  enabled,
  onCourseData,
  pollIntervalMs = PREPARING_POLL_INTERVAL_MS,
  maxPollMs = PREPARING_POLL_MAX_MS,
  pauseWhenHidden = true,
}: UseProactiveCoursePreparingPollOptions): {
  liveStatus: ProactiveStatusPayload | null;
  httpError: string | null;
} {
  const [liveStatus, setLiveStatus] = useState<ProactiveStatusPayload | null>(null);
  const [httpError, setHttpError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || canonicalLessonId == null) return;

    const statusUrl = `/api/lessons/${canonicalLessonId}/proactive-status`;
    const controller = new AbortController();
    let isActive = true;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let courseDataFetched = false;
    const startedAt = Date.now();

    const stop = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // The course tree only needs to be fetched once, on the transition into a
    // terminal state — everything rendered while waiting comes from liveStatus.
    const fetchCourseOnce = async () => {
      if (courseDataFetched) return;
      courseDataFetched = true;
      try {
        const courseRes = await getCourseDetailsApiV1CoursesCourseIdGet({
          path: { course_id: courseId },
          signal: controller.signal,
        });
        if (!isActive) return;
        if (courseRes.data) {
          onCourseData(courseRes.data as unknown as CourseDetail);
        }
      } catch (error) {
        if (!isActive) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        console.error("Failed to fetch course after preparing completed:", error);
      }
    };

    const tick = async () => {
      // Hard stop so the poll can never run forever; the UI shows a terminal
      // state once elapsed crosses PREPARING_POLL_MAX_MS.
      if (Date.now() - startedAt >= maxPollMs) {
        stop();
        return;
      }
      if (pauseWhenHidden && typeof document !== "undefined" && document.hidden) {
        return;
      }
      try {
        const statusRes = await getProactiveStatusApiV1LessonsLessonIdProactiveStatusGet({
          path: { lesson_id: canonicalLessonId },
          signal: controller.signal,
        });
        if (!isActive) return;

        const st = (statusRes.data || statusRes.error || {}) as Record<string, unknown> & { error?: string };
        if (statusRes.error) {
          setHttpError(
            typeof st.error === "string"
              ? st.error
              : `Could not check lesson status (${statusRes.response?.status})`,
          );
          setLiveStatus(null);
          return;
        }
        setHttpError(null);
        const normalized = normalizeProactiveStatusBody(st);
        setLiveStatus(normalized);

        const outcome = interpretProactiveFetchOutcome(normalized);
        if (outcome === "ready") {
          stop();
          await fetchCourseOnce();
        }
      } catch (error) {
        if (!isActive) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        console.error("Failed to poll proactive status:", error);
        setHttpError("Could not refresh status. Check your connection.");
        setLiveStatus(null);
      }
    };

    void tick();
    intervalId = setInterval(tick, pollIntervalMs);

    const onVisibilityChange = () => {
      if (typeof document !== "undefined" && !document.hidden) {
        void tick();
      }
    };
    if (pauseWhenHidden) {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      isActive = false;
      controller.abort();
      stop();
      if (pauseWhenHidden) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [
    canonicalLessonId,
    courseId,
    enabled,
    onCourseData,
    pollIntervalMs,
    maxPollMs,
    pauseWhenHidden,
  ]);

  if (!enabled || canonicalLessonId == null) {
    return { liveStatus: null, httpError: null };
  }
  return { liveStatus, httpError };
}

export type UseProactiveLessonPersonalizationOptions = {
  canonicalLessonId: number | null | undefined;
  contentReady: boolean;
  router: { refresh: () => void };
};

export function useProactiveLessonPersonalization({
  canonicalLessonId,
  contentReady,
  router,
}: UseProactiveLessonPersonalizationOptions): {
  phase: LessonPersonalizePhase;
  error: string | null;
  lastTaskId: string | null;
  statusRefreshing: boolean;
  lastStatusPayload: ProactiveStatusPayload | null;
  bannerHeadline: string | null;
  scheduleRetry: () => void;
  refreshStatus: () => Promise<void>;
} {
  const [phase, setPhase] = useState<LessonPersonalizePhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [lastTaskId, setLastTaskId] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const [lastStatusPayload, setLastStatusPayload] = useState<ProactiveStatusPayload | null>(
    null,
  );

  const scheduleRetry = useCallback(() => {
    setRetryToken((n) => n + 1);
    setPhase("idle");
    setError(null);
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!canonicalLessonId) return;
    setStatusRefreshing(true);
    try {
      const result = await fetchProactiveLessonStatus(canonicalLessonId);
      if (result.ok && result.outcome === "ready") {
        setPhase("done");
        setLastStatusPayload(result.payload);
        router.refresh();
        return;
      }
      if (result.ok && result.outcome === "failed") {
        setPhase("error");
        setLastStatusPayload(result.payload);
        setError("Personalization could not be completed. You can try again.");
        return;
      }
      if (result.ok) {
        setLastStatusPayload(result.payload);
      }
      if (!result.ok) {
        setPhase("error");
        setError(result.error);
      }
    } finally {
      setStatusRefreshing(false);
    }
  }, [canonicalLessonId, router]);

  useEffect(() => {
    if (contentReady) {
      setPhase("done");
      setError(null);
      setLastStatusPayload(null);
      return;
    }
    if (!canonicalLessonId) return;

    let cancelled = false;

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      setPhase("personalizing");
      setError(null);
      setLastStatusPayload(null);
      try {
        const { data: resData, error: reqError, response } = await triggerProactiveGenerationApiV1LessonsLessonIdProactiveGenerationPost({
          path: { lesson_id: canonicalLessonId },
        });
        const data = (resData || reqError || {}) as {
          task_id?: string;
          status?: string;
          error?: string;
        };
        if (cancelled) return;

        if (response?.status === 202 && data.task_id) {
          setLastTaskId(data.task_id);
        } else if (response?.status === 200 && data.status === "already_personalized") {
          setLastTaskId(null);
          router.refresh();
          return;
        } else if (reqError && response?.status !== 202 && response?.status !== 200) {
          setPhase("error");
          setError(data.error || "Could not start personalization.");
          return;
        } else {
          if (data.task_id) setLastTaskId(data.task_id);
        }

        setPhase("polling");
        const pollStarted = Date.now();
        let delayMs = LESSON_PROACTIVE_POLL_INITIAL_MS;
        let fastRounds = 0;

        while (!cancelled && Date.now() - pollStarted < LESSON_PROACTIVE_POLL_MAX_MS) {
          await sleep(delayMs);
          fastRounds += 1;
          if (fastRounds >= LESSON_PROACTIVE_POLL_FAST_ROUNDS) {
            delayMs = LESSON_PROACTIVE_POLL_STEADY_MS;
          }

          if (cancelled) return;
          const result = await fetchProactiveLessonStatus(canonicalLessonId);
          if (cancelled) return;

          if (result.ok) {
            setLastStatusPayload(result.payload);
          }

          if (!result.ok) {
            setPhase("error");
            setError(result.error);
            return;
          }
          if (result.outcome === "ready") {
            setPhase("done");
            router.refresh();
            return;
          }
          if (result.outcome === "failed") {
            setPhase("error");
            setError("Personalization could not be completed. You can try again.");
            return;
          }
        }

        if (cancelled) return;
        setPhase("polling_timeout");
        setError(null);
      } catch (e) {
        if (!cancelled) {
          console.error("[useProactiveLessonPersonalization] flow failed", e);
          setPhase("error");
          setError("Something went wrong. Please try again.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contentReady, canonicalLessonId, router, retryToken]);

  const bannerHeadline = useMemo(
    () => lessonPersonalizingHeadline(phase, lastStatusPayload),
    [phase, lastStatusPayload],
  );

  return {
    phase,
    error,
    lastTaskId,
    statusRefreshing,
    lastStatusPayload,
    bannerHeadline,
    scheduleRetry,
    refreshStatus,
  };
}
